"use client";

import React, { memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn-components/ui/table";
import { cn } from "@/lib/utils";

const components: Components = {
  table: ({ children }) => (
    <div className="my-2 max-w-full overflow-x-auto rounded-md border border-border">
      <Table className="text-[11px]">{children}</Table>
    </div>
  ),
  thead: ({ children }) => <TableHeader>{children}</TableHeader>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children, style }) => (
    <TableHead
      className="h-7 max-w-[12rem] truncate px-2 py-1 text-[10px]"
      style={style}
    >
      {children}
    </TableHead>
  ),
  td: ({ children, style }) => (
    <TableCell
      className="max-w-[12rem] truncate px-2 py-1 align-middle"
      style={style}
    >
      {children}
    </TableCell>
  ),
  p: ({ children }) => (
    <p className="mb-1.5 break-words last:mb-0">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="break-words leading-relaxed">{children}</li>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  code: ({ className, children }) => {
    const isBlock = (className ?? "").includes("language-");
    if (isBlock)
      return <code className={cn("font-mono", className)}>{children}</code>;
    return (
      <code className="break-all rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-1.5 max-w-full overflow-x-auto rounded-md border border-border bg-muted/40 p-2 text-[10px] leading-snug font-mono">
      {children}
    </pre>
  ),
  h1: ({ children }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>
  ),
  h2: ({ children }) => (
    <h4 className="text-[13px] font-semibold mt-2 mb-1">{children}</h4>
  ),
  h3: ({ children }) => (
    <h5 className="text-xs font-semibold mt-2 mb-1">{children}</h5>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-2 text-muted-foreground my-1.5">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-2 border-border" />,
};

const remarkPlugins = [remarkGfm];

function AgentMarkdownImpl({ text }: { text: string }) {
  return (
    <div className="min-w-0 max-w-full space-y-1 overflow-x-hidden text-xs leading-relaxed text-foreground">
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

export const AgentMarkdown = memo(AgentMarkdownImpl);
AgentMarkdown.displayName = "AgentMarkdown";
